import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { HiddenFileInput } from 'designSystem/components/HiddenFileInput'
import { FileDropZone } from 'features/assetLifecycleV2/shared/FileDropZone'
import type { FileUploadInput } from 'features/assetLifecycleV2/shared/fileUploadSelection'
import type { FimSubjectRecord } from 'features/fim/types'
import { useGeckoNotifications } from 'features/notifications'
import { Camera } from 'lucide-react'
import React, { type ReactNode, useCallback, useRef } from 'react'
import styled from 'styled-components'

import { electionTarget } from './photoElections'
import { photoLocator } from './photoLocation'
import { type UploadedPhoto, usePhotoService } from './photoService'
import { PhotoUploadRow } from './PhotoUploadRow'
import { electionKind, type PhotoSource } from './types'
import { type CommitResult, usePhotoUploadQueue } from './usePhotoUploadQueue'

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`

/** One toast per burst of attaches: each success replaces the last. */
const ATTACH_TOAST_KEY = 'subject-photo-attached'
const DUPLICATE_TOAST_KEY = 'subject-photo-duplicate'

const PHOTO_FILE_TYPES = 'image/*'

const toFile = (input: FileUploadInput): File =>
  input instanceof File ? input : input.file

/**
 * Every chosen or dropped photo is uploaded and attached with no further
 * click. Render only for a subject `electionTarget` resolves for.
 */
export const SubjectPhotoUploader: React.FC<{
  siteId: string
  subject: FimSubjectRecord
  source: PhotoSource
  onAttached: () => void
  retainAttached?: boolean
  renderAttachedAction?: (photoId: string) => ReactNode
  children?: ReactNode
}> = ({
  siteId,
  subject,
  source,
  onAttached,
  retainAttached = false,
  renderAttachedAction,
  children,
}) => {
  const { t } = useLingui()
  const { attachPhoto, removePhotoFromSubject } = usePhotoService()
  const { successNotification, infoNotification } = useGeckoNotifications()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const commit = useCallback(
    async ({ photoId, elections }: UploadedPhoto): Promise<CommitResult> => {
      const target = electionTarget(siteId, subject)
      if (elections.some((e) => e.subject_identity_norm === target?.norm)) {
        return 'already_attached'
      }
      await attachPhoto(photoId, {
        siteId,
        subject,
        label: subject.data.name,
        equipmentKind: electionKind(subject.data.kind),
      })
      if (!retainAttached) {
        successNotification(t`Photo attached.`, { key: ATTACH_TOAST_KEY })
      }
      return 'attached'
    },
    [attachPhoto, retainAttached, siteId, subject, successNotification, t]
  )
  const remove = useCallback(
    ({ photoId }: UploadedPhoto) =>
      removePhotoFromSubject(photoId, { siteId, subject }),
    [removePhotoFromSubject, siteId, subject]
  )
  const onDuplicate = useCallback(
    () =>
      infoNotification(t`This photo is already attached to this Subject.`, {
        key: DUPLICATE_TOAST_KEY,
      }),
    [infoNotification, t]
  )
  const queue = usePhotoUploadQueue(
    siteId,
    subject.subjectUid,
    source,
    commit,
    onAttached,
    { retainAttached, onDuplicate, remove }
  )

  const addFiles = useCallback(
    (inputs: FileUploadInput[]) => queue.addFiles(inputs.map(toFile)),
    [queue]
  )

  const openCamera = useCallback(() => {
    photoLocator.prime()
    fileInputRef.current?.click()
  }, [])

  const onFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) addFiles(Array.from(event.target.files))
      event.target.value = ''
    },
    [addFiles]
  )

  return (
    <FileDropZone onFiles={addFiles}>
      <Stack>
        {children}
        {queue.items
          .filter(
            (item) => retainAttached || item.phase !== 'attached' || item.error
          )
          .map((item) => (
            <PhotoUploadRow
              key={item.key}
              previewUrl={item.previewUrl}
              fileName={item.file.name}
              phase={item.phase}
              error={item.error}
              retryable={item.retryable}
              onRetry={() => queue.retryItem(item.key)}
              onDiscard={() => queue.discardItem(item.key)}
              onRemove={() => void queue.removeAttachedItem(item.key)}
              attachedAction={
                item.photoId && renderAttachedAction?.(item.photoId)
              }
            />
          ))}
        <Actions>
          <Button
            type='button'
            variant='outline'
            title={t`Photograph the physical equipment and attach it to this Subject`}
            onClick={openCamera}>
            <Camera data-icon='inline-start' />
            <Trans>Add photo</Trans>
          </Button>
        </Actions>
      </Stack>
      <HiddenFileInput
        ref={fileInputRef}
        accept={PHOTO_FILE_TYPES}
        capture='environment'
        aria-label={t`Take a photo`}
        onChange={onFileInputChange}
      />
    </FileDropZone>
  )
}
